import { useEffect, useState } from 'react';
import { useToggle } from '@dak/hooks';
import { Users, Plus, X, Lock, Globe } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { useUserStore } from '../stores/user-store';
import { useSharesStore } from '../stores/shares-store';
import { ConfirmModal, Input, Button, useToastStore } from '@dak/ui';
import type { Note } from '../types/note';

interface NoteSharingProps {
  note: Note;
  onTogglePrivate?: (isPrivate: boolean) => void;
}

export function NoteSharing({ note, onTogglePrivate }: NoteSharingProps) {
  const { user } = useAuthStore();
  const { planLimits } = useUserStore();
  const { showToast } = useToastStore();
  const {
    noteShares,
    uniqueShareCount,
    fetchNoteShares,
    addNoteShare,
    removeNoteShare,
    removeAllNoteShares,
    fetchUniqueShareCount,
  } = useSharesStore();

  const [newEmail, setNewEmail] = useState('');
  const isAdding = useToggle(false);
  const showAddForm = useToggle(false);
  const isMakingPrivate = useToggle(false);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const showOwnerEmail = useToggle(false);
  const [removeConfirm, setRemoveConfirm] = useState<{ userId: string; email: string } | null>(
    null,
  );
  const [showMakePrivateConfirm, setShowMakePrivateConfirm] = useState(false);

  const shares = noteShares[note.id] || [];
  const isOwner = note.user_id === user?.id;
  const canShare = planLimits.maxSharedUsers > 0;

  useEffect(() => {
    if (note.id) {
      fetchNoteShares(note.id);
    }
  }, [note.id, fetchNoteShares]);

  useEffect(() => {
    if (user?.id) {
      fetchUniqueShareCount(user.id);
    }
  }, [user?.id, fetchUniqueShareCount]);

  const handleAddShare = async () => {
    if (!user?.id || !newEmail.trim()) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      showToast('Please enter a valid email address.', 'error');
      return;
    }

    // Check if already shared
    if (shares.some((s) => s.email === newEmail.toLowerCase().trim())) {
      showToast('This note is already shared with this person.', 'error');
      return;
    }

    isAdding.setTrue();
    try {
      await addNoteShare(note.id, user.id, newEmail.trim());
      setNewEmail('');
      showAddForm.setFalse();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to share note', 'error');
    } finally {
      isAdding.setFalse();
    }
  };

  const handleRemoveShare = (userId: string, email: string) => {
    setRemoveConfirm({ userId, email });
  };

  const confirmRemoveShare = async () => {
    if (removeConfirm) {
      await removeNoteShare(note.id, removeConfirm.userId);
      setRemoveConfirm(null);
    }
  };

  const handleMakePrivateClick = () => {
    if (!note.is_private && shares.length > 0) {
      setShowMakePrivateConfirm(true);
    } else {
      onTogglePrivate?.(!note.is_private);
    }
  };

  const confirmMakePrivate = async () => {
    if (!user?.id) return;
    isMakingPrivate.setTrue();
    try {
      await removeAllNoteShares(note.id, user.id);
      onTogglePrivate?.(true);
    } catch {
      showToast('Failed to remove shares', 'error');
    } finally {
      isMakingPrivate.setFalse();
      setShowMakePrivateConfirm(false);
    }
  };

  // Non-owners see a read-only view showing who shared it
  if (!isOwner) {
    const ownerDisplay = note.owner_name || note.owner_email || 'the owner';
    const hasEmail = !!note.owner_email && note.owner_name;

    return (
      <div className="px-4 py-3 border-t border-border">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-accent" />
          <div className="flex-1">
            <p className="text-sm text-text-muted">
              Shared with you by{' '}
              <Button
                variant="ghost"
                onClick={hasEmail ? showOwnerEmail.toggle : undefined}
                className="font-medium text-accent p-0 h-auto inline"
              >
                {ownerDisplay}
              </Button>
            </p>
            {showOwnerEmail.value && hasEmail && (
              <p className="text-xs mt-0.5 text-text-muted">{note.owner_email}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-border">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          {note.is_private ? (
            <Lock size={16} className="text-text-muted" />
          ) : (
            <Globe size={16} className="text-warning" />
          )}
          <span className="text-sm font-medium text-text">
            {note.is_private ? 'Private Note' : 'Shared Note'}
          </span>
        </div>

        {onTogglePrivate && (
          <Button variant="secondary" size="sm" onClick={handleMakePrivateClick}>
            {note.is_private ? 'Make Shared' : 'Make Private'}
          </Button>
        )}
      </div>

      {/* Share list (only for non-private notes) */}
      {!note.is_private && (
        <div className="px-4 pb-3">
          {!canShare ? (
            <p className="text-[13px] text-text-muted">Upgrade to share notes with others</p>
          ) : (
            <>
              {/* Existing shares */}
              {shares.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs mb-1.5 text-text-muted">Shared with:</p>
                  {shares.map((share) => {
                    const showName = share.display_name && share.display_name !== share.email;
                    const isExpanded = expandedUserId === share.user_id;

                    return (
                      <div
                        key={share.user_id}
                        className="flex items-center justify-between py-2 border-b border-border"
                      >
                        <Button
                          variant="ghost"
                          onClick={
                            showName
                              ? () => setExpandedUserId(isExpanded ? null : share.user_id)
                              : undefined
                          }
                          className="flex-1 text-left justify-start h-auto p-0"
                        >
                          <div>
                            <span className="text-sm text-text">
                              {share.display_name || share.email}
                            </span>
                            {isExpanded && showName && (
                              <span className="block text-xs mt-0.5 text-text-muted">
                                {share.email}
                              </span>
                            )}
                          </div>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleRemoveShare(share.user_id, share.email)}
                        >
                          <X size={16} className="text-text-muted" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add share form */}
              {showAddForm.value ? (
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Email address"
                      autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && handleAddShare()}
                    />
                  </div>
                  <Button
                    onClick={handleAddShare}
                    disabled={isAdding.value || !newEmail.trim()}
                    loading={isAdding.value}
                  >
                    Add
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => {
                      showAddForm.setFalse();
                      setNewEmail('');
                    }}
                  >
                    <X size={18} className="text-text-muted" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  onClick={() => showAddForm.setTrue()}
                  className="flex items-center gap-1.5 text-warning p-0 h-auto"
                >
                  <Plus size={16} />
                  <span className="text-sm">Share with someone</span>
                </Button>
              )}

              {/* Share count */}
              <p className="text-xs mt-2 text-text-muted">
                Sharing with {uniqueShareCount} / {planLimits.maxSharedUsers} people total
              </p>
            </>
          )}
        </div>
      )}

      {/* Remove share confirmation */}
      <ConfirmModal
        open={removeConfirm !== null}
        title="Remove access?"
        message={`${removeConfirm?.email || ''} will no longer be able to view or edit this note.`}
        confirmText="Remove"
        variant="danger"
        onConfirm={confirmRemoveShare}
        onClose={() => setRemoveConfirm(null)}
      />

      {/* Make private confirmation */}
      <ConfirmModal
        open={showMakePrivateConfirm}
        title="Make note private?"
        message={`This will remove access for ${shares.length === 1 ? '1 person' : `${shares.length} people`} who currently have access to this note.`}
        confirmText={isMakingPrivate.value ? 'Removing...' : 'Make Private'}
        variant="danger"
        onConfirm={confirmMakePrivate}
        onClose={() => setShowMakePrivateConfirm(false)}
      />
    </div>
  );
}
